import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import Toast from '../components/Toast.jsx';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};

let toastIdCounter = 0;

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const seenMessagesRef = useRef(new Set());
    const recentToastsRef = useRef(new Map());
    const sessionSeenToastsRef = useRef(new Set()); // Permanent session-wide seen toasts

    const isLoadingLikeMessage = useCallback((message) => {
        const text = String(message || '').toLowerCase();
        return /\b(opening|loading|navigating|redirecting|switching|refreshing|fetching|processing|waiting)\b/.test(text);
    }, []);

    const sanitizeErrorToast = useCallback((message) => {
        const text = String(message == null ? 'Unexpected error.' : message).replace(/\s+/g, ' ').trim();
        const lower = text.toLowerCase();

        if (!text) return 'An error occurred. Please try again.';

        if (
            text.length > 140 ||
            /traceback|exception|keyerror|typeerror|referenceerror|syntaxerror|internal server error|mongodb|mongo|sql|smtp|mailgun|stack trace|at line|\bundefined\b|\bnull\b|\/app\//i.test(text) ||
            /[{}<>]|\bcommand\b|\berror:\b|\bfailure\b/.test(lower)
        ) {
            return 'An error occurred. Please try again.';
        }

        return text;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const isToastSuppressed = useCallback((node) => {
        if (!(node instanceof Element)) {
            return false;
        }

        return Boolean(node.closest('[data-toast-suppress="true"]'));
    }, []);

    const showToast = useCallback((message, type = 'info', duration = 4000) => {
        const text = typeof message === 'string'
            ? message
            : (message == null ? 'Unexpected message.' : JSON.stringify(message));
        const normalizedText = text.replace(/\s+/g, ' ').trim();
        const normalizedLower = normalizedText.toLowerCase();

        const normalizedType = ['success', 'error', 'warning', 'info'].includes(type) ? type : 'info';

        if (isLoadingLikeMessage(normalizedText)) {
            return;
        }

        const finalText = normalizedType === 'error'
            ? sanitizeErrorToast(normalizedText)
            : normalizedText;

        // Skip non-actionable navigation/progress toasts like "opening/loading page...".
        if (
            normalizedType === 'info' &&
            /\b(opening|loading|navigating|redirecting|switching)\b/.test(normalizedLower) &&
            /\b(page|screen|view)\b/.test(normalizedLower)
        ) {
            return;
        }

        const normalizedDuration = Number.isFinite(duration) ? Math.max(2000, Math.min(duration, 12000)) : 4000;
        const dedupeKey = `${normalizedType}:${finalText.toLowerCase()}`;
        
        // Strictly "ONLY ONCE" for the entire session
        if (sessionSeenToastsRef.current.has(dedupeKey)) {
            return;
        }
        sessionSeenToastsRef.current.add(dedupeKey);

        const id = ++toastIdCounter;
        setToasts(prev => {
            if (prev.some(t => t.type === normalizedType && t.message === finalText)) {
                return prev;
            }
            const next = [...prev, { id, message: finalText, type: normalizedType, duration: normalizedDuration }];
            return next.slice(-5);
        });
    }, [isLoadingLikeMessage, sanitizeErrorToast]);

    const showSuccess = useCallback((message, duration = 4000) => {
        showToast(message, 'success', duration);
    }, [showToast]);

    const showError = useCallback((message, duration = 5000) => {
        showToast(message, 'error', duration);
    }, [showToast]);

    const showWarning = useCallback((message, duration = 4500) => {
        showToast(message, 'warning', duration);
    }, [showToast]);

    const showInfo = useCallback((message, duration = 4000) => {
        showToast(message, 'info', duration);
    }, [showToast]);

    useEffect(() => {
        const originalAlert = window.alert;

        window.alert = (message) => {
            const text = typeof message === 'string' ? message : String(message);
            const lowered = text.toLowerCase();
            const type = lowered.includes('fail') || lowered.includes('error') ? 'error' : 'info';
            showToast(text, type, 5000);
        };

        return () => {
            window.alert = originalAlert;
        };
    }, [showToast]);

    useEffect(() => {
        const handleAppErrorToast = (event) => {
            showToast('An error occurred. Please try again.', 'error', 6000);
        };

        window.addEventListener('app-error-toast', handleAppErrorToast);
        return () => {
            window.removeEventListener('app-error-toast', handleAppErrorToast);
        };
    }, [showToast]);

    useEffect(() => {
        const selector = '.error-message, .success-message, .error-container, .modern-error-message, .modern-success-message, .status-message, .alert-error, .alert-success, .notification, .error-notification, .success-notification, [role=\"alert\"], [data-toast-inline]';

        const getTypeFromElement = (el) => {
            const className = (el.className || '').toString().toLowerCase();
            const text = (el.textContent || '').toLowerCase();
            if (className.includes('success') || text.includes('success')) {
                return 'success';
            }
            if (className.includes('warn')) {
                return 'warning';
            }
            return 'error';
        };

        const emitFromNode = (root) => {
            if (!(root instanceof Element)) {
                return;
            }

            if (isToastSuppressed(root)) {
                return;
            }

            const candidates = root.matches(selector)
                ? [root, ...Array.from(root.querySelectorAll(selector))]
                : Array.from(root.querySelectorAll(selector));

            candidates.forEach((el) => {
                if (isToastSuppressed(el)) {
                    return;
                }

                const text = (el.textContent || '').trim();
                if (!text || text.length < 3) {
                    return;
                }

                if (isLoadingLikeMessage(text)) {
                    return;
                }

                const key = `${getTypeFromElement(el)}:${text}`;
                if (seenMessagesRef.current.has(key)) {
                    return;
                }

                seenMessagesRef.current.add(key);
                showToast(text, getTypeFromElement(el), 5000);
                el.classList.add('cx-toast-suppressed');
                // Removed timeout to keep messages in seenMessagesRef forever (session-wide)
            });
        };

        emitFromNode(document.body);

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => emitFromNode(node));
                }
                if (mutation.type === 'characterData' && mutation.target?.parentElement) {
                    emitFromNode(mutation.target.parentElement);
                }
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        return () => observer.disconnect();
    }, [showToast]);

    return (
        <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo }}>
            {children}
            <div className="toast-container">
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        duration={toast.duration}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};
